/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Category, Constituent } from '../market-board.types';
import {
  pickDelta,
  pickScore,
  pickVolume,
  sentimentBucket,
} from '../market-board.utils';
import {
  CountryBoardDataService,
  DEFAULT_TIMEFRAME,
  KeyEventVM,
  PanelTimeframe,
} from '../news-market-countries/country-board-data.service';

/**
 * The market-board detail panel: sentiment stats, distribution histogram, key
 * events and constituent movers for one entity, over a given timeframe. Reused
 * by the country map (7-day window) and the sentiment heatmap (a clicked month).
 *
 * It loads its own constituents + key events from `CountryBoardDataService` when
 * `entity` changes, scoped to `timeframe`. In `aggregate` mode (the heatmap's
 * average row) the caller supplies a prebuilt entity — constituents, children
 * and counts — and no fetch is made.
 */
@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-market-detail-panel',
  templateUrl: './market-detail-panel.component.html',
  styleUrls: ['./market-detail-panel.component.scss'],
})
export class MarketDetailPanelComponent implements OnChanges, OnDestroy {
  @Input() entity: Category | null = null;
  @Input() timeframe: PanelTimeframe = DEFAULT_TIMEFRAME;
  /** Optional caption under the title (e.g. a month label); falls back to "avg sentiment". */
  @Input() timeframeLabel = '';
  @Input() sentimentParams: Record<string, any> = {};
  /** Aggregate row: the caller prebuilt constituents/children; skip fetching. */
  @Input() aggregate = false;

  @Output() closed = new EventEmitter<void>();

  showAllDecliners = false;
  showAllGainers = false;

  keyEvents: KeyEventVM[] = [];
  keyEventsLoading = false;

  readonly skeletonBars = Array.from({ length: 20 });
  readonly skeletonRows = Array.from({ length: 5 });

  private destroy$ = new Subject<void>();

  constructor(
    private data: CountryBoardDataService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entity'] && this.entity) {
      this.showAllDecliners = false;
      this.showAllGainers = false;
      this.keyEvents = [];
      if (this.aggregate) {
        // Aggregate row: constituents/children already supplied, no key events.
        this.entity.loading = false;
        this.keyEventsLoading = false;
      } else {
        this.loadFullConstituents(this.entity);
        this.loadKeyEvents(this.entity);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closed.emit();
  }

  trackByKey(_i: number, c: Constituent): string {
    return c.compoundKey;
  }

  averageClass(avg: number): string {
    return sentimentBucket(avg);
  }

  toggleShowAllDecliners(): void {
    this.showAllDecliners = !this.showAllDecliners;
  }

  toggleShowAllGainers(): void {
    this.showAllGainers = !this.showAllGainers;
  }

  allDecliners(cat: Category | null): Constituent[] {
    if (!cat) return [];
    return cat.constituents.filter((c) => c.impact < 0);
  }

  allGainers(cat: Category | null): Constituent[] {
    if (!cat) return [];
    return cat.constituents
      .filter((c) => c.impact >= 0)
      .slice()
      .reverse();
  }

  visibleDecliners(cat: Category | null, n = 5): Constituent[] {
    const all = this.allDecliners(cat);
    return this.showAllDecliners ? all : all.slice(0, n);
  }

  visibleGainers(cat: Category | null, n = 5): Constituent[] {
    const all = this.allGainers(cat);
    return this.showAllGainers ? all : all.slice(0, n);
  }

  navigateToConstituent(c: Constituent): void {
    if (!c.compoundKey) return;
    const [type, ...rest] = c.compoundKey.split(':');
    const alphaId = rest.join(':');
    if (!type || !alphaId) return;

    // No event selected → drop any event/sort params so the chart opens clean.
    this.openMarketChartTab({ type, id: alphaId }, [
      'tab',
      'window',
      'sort',
      'period',
      'tag',
      'news',
      'eventid',
      'eventype',
      'scoreevent',
      'newstype',
    ]);
  }

  // Open the entity's market chart with this key event selected.
  navigateToKeyEvent(e: KeyEventVM): void {
    const cat = this.entity;
    if (!cat || !e.id) return;
    this.openMarketChartTab(
      {
        type: this.entityType(cat),
        id: this.requestId(cat),
        sort: 'score',
        newstype: 'Chronological',
        eventid: e.id,
        eventype: e.type,
      },
      ['tab', 'window', 'period', 'tag', 'news', 'scoreevent'],
    );
  }

  // Open the market chart in a new tab: board filters + `overrides`, minus `dropKeys`.
  private openMarketChartTab(
    overrides: Record<string, any>,
    dropKeys: string[],
  ): void {
    const queryParams: Record<string, any> = {
      ...this.sentimentParams,
      ...overrides,
    };
    dropKeys.forEach((k) => delete queryParams[k]);

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/cockpit/news/market/chart'], {
        queryParams,
      }),
    );
    window.open(url, '_blank', 'noopener');
  }

  distributionBins(cat: Category | null): { count: number; center: number }[] {
    if (!cat) return [];
    const binCount = 20;
    const bins: { count: number; center: number }[] = Array.from(
      { length: binCount },
      (_, i) => ({ count: 0, center: (i + 0.5) * (100 / binCount) }),
    );
    (cat.children ?? []).forEach((node: any) => {
      const s = pickScore(node);
      if (!Number.isFinite(s)) return;
      const pct = Math.max(0, Math.min(99.999, s * 100));
      const idx = Math.floor(pct / (100 / binCount));
      bins[idx].count += 1;
    });
    return bins;
  }

  distributionStdev(cat: Category | null): number {
    if (!cat) return 0;
    const scores = (cat.children ?? [])
      .map((c: any) => pickScore(c))
      .filter((v: number) => Number.isFinite(v))
      .map((v: number) => v * 100);
    if (scores.length < 2) return 0;
    const mean =
      scores.reduce((a: number, b: number) => a + b, 0) / scores.length;
    const variance =
      scores.reduce(
        (acc: number, s: number) => acc + (s - mean) * (s - mean),
        0,
      ) / scores.length;
    return Math.round(Math.sqrt(variance));
  }

  distributionPercent(cat: Category | null, bucket: 'neg' | 'pos'): number {
    if (!cat) return 0;
    const total = cat.constituentCount || cat.pos + cat.neu + cat.neg;
    if (!total) return 0;
    const n = bucket === 'neg' ? cat.neg : cat.pos;
    return Math.round((n / total) * 100);
  }

  maxBinCount(bins: { count: number }[]): number {
    return bins.reduce((m, b) => Math.max(m, b.count), 0) || 1;
  }

  private loadKeyEvents(cat: Category): void {
    const id = this.requestId(cat).toLowerCase();
    this.keyEvents = [];
    this.keyEventsLoading = true;
    this.data
      .getKeyEvents$(id, this.entityType(cat), this.timeframe)
      .pipe(takeUntil(this.destroy$))
      .subscribe((events) => {
        this.keyEvents = events;
        this.keyEventsLoading = false;
        this.cdr.markForCheck();
      });
  }

  private loadFullConstituents(cat: Category): void {
    const id = this.requestId(cat).toLowerCase();
    cat.loading = true;
    this.data
      .getChildren$(id, this.entityType(cat), this.timeframe)
      .pipe(takeUntil(this.destroy$))
      .subscribe((children) => {
        cat.children = children;
        this.recomputeAggregates(cat);
        cat.constituents = this.buildConstituents(cat);
        cat.loading = false;
        this.cdr.markForCheck();
      });
  }

  private recomputeAggregates(cat: Category): void {
    const nodes = (cat.children ?? []) as any[];
    let neg = 0;
    let neu = 0;
    let pos = 0;
    const scores: number[] = [];

    nodes.forEach((node: any) => {
      const s = pickScore(node);
      if (!Number.isFinite(s)) return;
      scores.push(s);
      const b = sentimentBucket(s * 100);
      if (b === 'pos') pos += 1;
      else if (b === 'neu') neu += 1;
      else neg += 1;
    });

    const covered = scores.length;
    cat.constituentCount = covered || nodes.length;
    cat.neg = neg;
    cat.neu = neu;
    cat.pos = pos;

    if (covered > 0) {
      const min = Math.min(...scores);
      const max = Math.max(...scores);
      cat.spread = Math.round((max - min) * 100);
    }
  }

  private buildConstituents(cat: Category): Constituent[] {
    const nodes = (cat.children ?? []) as any[];
    const constituents: Constituent[] = nodes
      .map((node) => {
        const entity = node?.entity ?? {};
        const key = entity.compound_key;
        if (!key) return null;
        const score = pickScore(node);
        const delta = pickDelta(node);
        const volume = pickVolume(node);
        const bucket = sentimentBucket(score * 100);
        const impact = Math.round((score - cat.average / 100) * 100);
        return {
          compoundKey: key,
          name: entity.name ?? key,
          score: Math.round((score || 0) * 100),
          bucket,
          volume,
          impact,
          delta: Number.isFinite(delta) ? Math.round(delta * 100) : 0,
          topEvent: node.top_event
            ? {
                label:
                  node.top_event.label ??
                  node.top_event.name ??
                  node.top_event.id ??
                  '',
                volume: node.top_event.volume ?? node.top_event.count ?? 0,
              }
            : undefined,
        } as Constituent;
      })
      .filter((c): c is Constituent => !!c);

    return constituents
      .filter((c) => Number.isFinite(c.score))
      .sort((a, b) => a.score - b.score);
  }

  private requestId(cat: Category): string {
    const ck = cat.compoundKey;
    if (ck && ck.includes(':')) return ck.split(':').slice(1).join(':');
    return cat.id;
  }

  // Data-API entity type for the panel's requests (country / industry / index).
  private entityType(cat: Category): string {
    const t = (cat.type || '').toLowerCase();
    return t === 'industry' || t === 'index' ? t : 'country';
  }
}
